import type React from 'react';
import { useCallback, useRef } from 'react';

import { isNavDrawerOpen } from '../navDrawer';

type Point = { x: number; y: number };

/**
 * Fires `onLongPress` with the touch point once the finger is held roughly
 * still for `delay` ms. Cancels if the finger lifts early (a tap) or moves past
 * `moveThreshold` px (a scroll). Touch-only by design: desktop keeps its hover
 * affordances, so no mouse handlers are attached and nothing changes there.
 *
 * Consumers must also apply `select-none` + `-webkit-touch-callout:none` to the
 * pressed element (gated to touch, e.g. `no-hover:*`) so iOS doesn't start a
 * text selection or show the callout menu during the hold. That CSS is
 * touch-scoped on purpose — this hook intentionally does not touch `contextmenu`,
 * so desktop right-click (copy, etc.) keeps working.
 */
export function useLongPress(
  onLongPress: (point: Point) => void,
  { delay = 450, moveThreshold = 16 }: { delay?: number; moveThreshold?: number } = {},
) {
  // `moveThreshold` defaults to 16px: a finger held for the full `delay` drifts a
  // few px naturally, and a 10px window cancelled those legitimate holds (the
  // "held it and nothing happened" flakiness). 16px still distinguishes a hold
  // from a scroll/drag, which travels much further.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPoint = useRef<Point | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    startPoint.current = null;
  }, []);

  const onTouchStart = useCallback(
    (event: React.TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const point = { x: touch.clientX, y: touch.clientY };
      startPoint.current = point;
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        timer.current = null;
        // Never pop a menu on top of the open sidebar drawer (#2021): a hold on
        // the page underneath it is not a request for the page's actions.
        if (isNavDrawerOpen()) {
          return;
        }
        onLongPress(point);
      }, delay);
    },
    [onLongPress, delay],
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      const start = startPoint.current;
      const touch = event.touches[0];
      if (!start || !touch) {
        return;
      }
      if (
        Math.abs(touch.clientX - start.x) > moveThreshold ||
        Math.abs(touch.clientY - start.y) > moveThreshold
      ) {
        cancel();
      }
    },
    [cancel, moveThreshold],
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  };
}
